import NextSeo from 'next-seo';
import Head from 'next/head';

import { SpaTreatments } from '@features/spa/pages/SpaTreatments';
import { useTranslations } from '@lang';
import * as journeyActions from '@store/yourJourney/actions';

import { getSpaSettingsPrefetch, getSpaTreatmentsPagePrefetch } from '@features/spa/hooks';
import { getSpaCategoriesPrefetch } from '@features/spa/hooks/useGetSpaCategories';
import { CategoryFilterValue } from '@features/spa/hooks/useSpaEntityFilter';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
import { NextPage, NextPageContext } from 'next';
import { Router } from 'shared-routing';
import { Property } from 'shared-types/Property';

type SpaTreatmentsPageProps = {
  propertyTitle: string;
  socialFallbackImage: {
    url: string;
  };
  language: string;
  url: string;
  categoryId: CategoryFilterValue;
};

const SpaTreatmentsPage: NextPage<SpaTreatmentsPageProps> = ({
  categoryId,
  language,
  propertyTitle = '',
  socialFallbackImage = { url: '' },
  url,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.spaTreatments');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <SpaTreatments categoryId={categoryId} language={language} />
    </>
  );
};

SpaTreatmentsPage.getInitialProps = async ({
  property,
  store,
  language,
  url,
  res,
  query,
}: NextPageContext & {
  property: Property;
  store: any;
  language: string;
  url: string;
  router: any;
}): Promise<SpaTreatmentsPageProps & { dehydratedState: DehydratedState }> => {
  if (!property.isSpaIntegrated) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }
  const client = new QueryClient();
  const { fromDate } = yourJourneySelectors.getParsedJourneyParams(store.getState());
  const spaDate = yourJourneySelectors.getActualSpaDateValue(store.getState());

  const spaDateToSet = spaDate || fromDate;
  const isServer = !!res;

  await Promise.all([
    store.dispatch(journeyActions.changeJourneyParam('spaDate', spaDateToSet)),
    isServer && getSpaCategoriesPrefetch(client, property.id, 'treatments', language),
    getSpaTreatmentsPagePrefetch(client, property.id, language),
    getSpaSettingsPrefetch(client, property.id),
  ]);

  return {
    url,
    language,
    categoryId:
      query.category === UNCATEGORIZED_VALUE
        ? UNCATEGORIZED_VALUE
        : query.category
        ? Number.parseInt(query.category as string, 10)
        : null,
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  };
};

export default SpaTreatmentsPage;
